import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { currencyFormatter } from "@common/helpers/currency";
import { MilestoneReviewColumn } from "@common/types/views/milestoneReviewView";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Link } from "@design-system/Link/Link";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { ViewConfig } from "@lib/views";
import styles from "./RecognitionReviewCells.module.scss";
import { MilestoneReviewRow } from "./milestoneReviewHelpers";
import { formatEffectiveDate } from "./recognitionReviewHelpers";

export interface MilestoneReviewTableCallbacks {
    onViewSchedule: (row: MilestoneReviewRow) => void;
    getPostingDate: (row: MilestoneReviewRow) => string;
    onPostingDateChange: (row: MilestoneReviewRow, postingDate: string) => void;
}

type MilestoneCellContext = CellContext<
    ViewTableFeatures,
    MilestoneReviewRow,
    unknown
>;

const ScheduleCell = ({ row }: MilestoneCellContext) => (
    <ViewTableCell>{row.original.scheduleName}</ViewTableCell>
);
const SourceCell = ({ row, table }: MilestoneCellContext) => {
    const meta = useViewTableMeta<MilestoneReviewTableCallbacks, unknown>(
        table,
    );
    const onViewSchedule = meta.callbacks?.onViewSchedule;
    return (
        <ViewTableCell>
            {onViewSchedule ? (
                <Link onClick={() => onViewSchedule(row.original)}>
                    {row.original.sourceLabel}
                </Link>
            ) : (
                row.original.sourceLabel
            )}
        </ViewTableCell>
    );
};
const MilestoneCell = ({ row }: MilestoneCellContext) => (
    <ViewTableCell>
        {row.original.sortOrder}. {row.original.name}
    </ViewTableCell>
);
const RecognizedCell = ({ row }: MilestoneCellContext) => (
    <ViewTableCell>
        <span className={styles.amount}>
            {currencyFormatter.format(row.original.plannedAmount)}
        </span>
    </ViewTableCell>
);
const ExpectedDateCell = ({ row }: MilestoneCellContext) => (
    <ViewTableCell>
        {formatEffectiveDate(row.original.expectedDate)}
    </ViewTableCell>
);
const PostingDateCell = ({ row, table }: MilestoneCellContext) => {
    const meta = useViewTableMeta<MilestoneReviewTableCallbacks, unknown>(
        table,
    );
    const callbacks = meta.callbacks;
    if (!callbacks) {
        return (
            <ViewTableCell>
                {formatEffectiveDate(row.original.expectedDate)}
            </ViewTableCell>
        );
    }
    return (
        <ViewTableCell>
            <DatePicker
                mode="day"
                variant="button"
                clearable={false}
                value={callbacks.getPostingDate(row.original)}
                onValueChange={(value) => {
                    if (value) {
                        callbacks.onPostingDateChange(row.original, value);
                    }
                }}
            />
        </ViewTableCell>
    );
};

export const milestoneReviewViewConfig: ViewConfig<
    MilestoneReviewRow,
    MilestoneReviewColumn,
    never
> = {
    columns: {
        [MilestoneReviewColumn.SCHEDULE]: {
            accessorKey: "scheduleName",
            name: "Schedule",
            header: "Schedule",
            cell: ScheduleCell,
            immutable: true,
            minSize: 160,
            maxSize: 320,
        },
        [MilestoneReviewColumn.SOURCE]: {
            accessorKey: "sourceLabel",
            name: "Source",
            header: "Source",
            cell: SourceCell,
            minSize: 160,
            maxSize: 320,
        },
        [MilestoneReviewColumn.MILESTONE]: {
            accessorKey: "name",
            name: "Milestone",
            header: "Milestone",
            cell: MilestoneCell,
            minSize: 180,
            maxSize: 400,
        },
        [MilestoneReviewColumn.RECOGNIZED]: {
            accessorKey: "plannedAmount",
            name: "Recognized",
            header: "Recognized",
            cell: RecognizedCell,
            ...AMOUNT_COLUMN_SIZE,
        },
        [MilestoneReviewColumn.EXPECTED_DATE]: {
            accessorKey: "expectedDate",
            name: "Expected date",
            header: "Expected date",
            cell: ExpectedDateCell,
            minSize: 140,
            maxSize: 180,
        },
        [MilestoneReviewColumn.POSTING_DATE]: {
            accessorKey: "periodId",
            name: "Posting date",
            header: "Posting date",
            cell: PostingDateCell,
            minSize: 180,
            maxSize: 240,
        },
    },
};
